import { useEffect, useRef } from "react";

import { getDocument, getWindow } from "@thelabnyc/thelabui/src/utils/dom";

interface IDimensions {
    width: number;
    height: number;
}

interface IBodyResizeEvent {
    type: "width" | "height" | "both";
    prevSize: IDimensions;
    newSize: IDimensions;
}

export type OnResizeHandler = (event: IBodyResizeEvent) => void;

export const WindowResizeSensor = (props: {
    onResize: OnResizeHandler;
}): null => {
    const sizeRef = useRef<IDimensions>(readBodyDimensions());
    const onResizeRef = useRef<OnResizeHandler>(props.onResize);

    const getChangeType = (
        prevSize: IDimensions,
        newSize: IDimensions,
    ): IBodyResizeEvent["type"] | null => {
        const changedWidth = prevSize.width !== newSize.width;
        const changedHeight = prevSize.height !== newSize.height;
        if (changedWidth && changedHeight) {
            return "both";
        }
        if (changedWidth) {
            return "width";
        }
        if (changedHeight) {
            return "height";
        }
        return null;
    };

    useEffect(() => {
        onResizeRef.current = props.onResize;
    }, [props.onResize]);

    useEffect(() => {
        const window = getWindow();
        if (!window) return;

        const handleResize = (): void => {
            const newSize = readBodyDimensions();
            const prevSize = sizeRef.current;
            const changeType = getChangeType(prevSize, newSize);
            if (changeType) {
                onResizeRef.current({
                    type: changeType,
                    prevSize: prevSize,
                    newSize: newSize,
                });
            }
            sizeRef.current = newSize;
        };

        // Align the tracked size with the current DOM before we start listening.
        sizeRef.current = readBodyDimensions();
        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);
    return null;
};

function readBodyDimensions(): IDimensions {
    const document = getDocument();
    return {
        width: document?.body.offsetWidth ?? 0,
        height: document?.body.offsetHeight ?? 0,
    };
}
